import { combinaBusca } from 'utils/buscaAproximada'
import clientService from '../client'
import processClient from '../processes'
import listSchedule from '../schedule/listSchedule'
import { getAuthHeaders } from 'utils/requests'
import filesApi from '../files'

/**
 * Busca global por processos, clientes, compromissos da agenda e arquivos
 * @param {string} query - Termo de busca
 * @returns {Promise} Resultados da busca
 */
export const VAZIO = { processes: [], clients: [], schedules: [], files: [] }

export const globalSearch = async (query) => {
  if (!query || query.trim().length < 2) {
    return VAZIO
  }

  try {
    const [processesResponse, clientsResponse, schedulesResponse, filesResponse] = await Promise.allSettled([
      searchProcesses(query),
      searchClients(query),
      searchSchedules(query),
      searchFiles(query)
    ])

    const processes = processesResponse.status === 'fulfilled' ? processesResponse.value : []
    const clients = clientsResponse.status === 'fulfilled' ? clientsResponse.value : []
    const schedules = schedulesResponse.status === 'fulfilled' ? schedulesResponse.value : []
    const files = filesResponse.status === 'fulfilled' ? filesResponse.value : []

    return { processes, clients, schedules, files }
  } catch (error) {
    console.error('Erro na busca global:', error)
    return VAZIO
  }
}

/**
 * Arquivos do escritório pelo nome (procuração, RG, petição...)
 */
const searchFiles = async (query) => {
  const response = await filesApi.get('/busca', { headers: getAuthHeaders(), params: { q: query.trim(), limit: 8 } })
  return response.data?.assets || []
}

/**
 * Busca todo o histórico da agenda por título de compromisso, número de processo, etiqueta/categoria ou responsável.
 */
const searchSchedules = async (query) => {
  try {
    const response = await listSchedule({ search: query, limit: 10 })
    let schedules = response.data?.schedules || []

    // Se a busca direta retornar vazia, realiza uma verificação auxiliar nos compromissos recente/futuros
    if (!schedules.length) {
      const allRes = await listSchedule({ limit: 50 })
      const allList = allRes.data?.schedules || []
      schedules = allList.filter((s) => combinaBusca([s.title, s.processNumber, s.category, s.responsavelNames || s.ResponsavelNome, s.description], query)).slice(0, 10)
    }

    return schedules
  } catch (error) {
    console.error('Erro ao buscar compromissos da agenda:', error)
    return []
  }
}

/**
 * Busca processos por número, responsável ou nome do cliente
 * @param {string} query - Termo de busca
 * @returns {Promise} Lista de processos
 */
const searchProcesses = async (query) => {
  try {
    const config = {
      headers: getAuthHeaders(),
      params: {}
    }

    // Se parece com número de processo (contém números)
    if (/\d/.test(query)) {
      config.params.NProcesso = query
    } else {
      // Busca por nome do cliente ou responsável
      config.params.PartePROPrincipalNome = query
    }

    config.params.limit = 10 // Limitar resultados

    const response = await processClient.get('/find', config)
    return response.data?.process || []
  } catch (error) {
    console.error('Erro ao buscar processos:', error)
    return []
  }
}

/**
 * Busca clientes por nome ou CPF/CNPJ
 * @param {string} query - Termo de busca
 * @returns {Promise} Lista de clientes
 */
const searchClients = async (query) => {
  try {
    const config = {
      headers: getAuthHeaders(),
      params: {
        limit: 5 // Limitar resultados
      }
    }

    // Remove caracteres especiais do CPF/CNPJ para comparação
    const cleanQuery = query.replace(/[.\-/]/g, '')
    
    // Se parece com CPF/CNPJ (apenas números ou números com formatação)
    if (/^[\d.\-/]+$/.test(query) || /^\d+$/.test(cleanQuery)) {
      config.params.cpf = cleanQuery
    } else {
      // Busca por nome
      config.params.nome = query
    }

    const response = await clientService.get('/list', config)
    return response.data?.clients || []
  } catch (error) {
    console.error('Erro ao buscar clientes:', error)
    return []
  }
}

export default globalSearch
